/**
 * Mini viewer equirretangular pra dentro das lentes do headset da landing.
 *
 * Por que não o createPanorama de panorama.ts: aqui a cena TROCA no meio da
 * piscada (setImage), e o viewer grande não expõe troca de textura — recriar
 * dois contextos WebGL a cada blink seria pagar caro pra esconder uma troca
 * que um texImage2D resolve. Este é um quad de tela cheia: o fragment calcula
 * a direção do raio por pixel e amostra a equirect. Sem drag — é cenário.
 */

const VS = 'attribute vec2 p;varying vec2 v;void main(){v=p;gl_Position=vec4(p,0.,1.);}';
const FS = `precision mediump float;varying vec2 v;uniform sampler2D t;uniform float yaw,pitch,fov,aspect;
void main(){
  float py=v.y*tan(fov*0.5), px=v.x*tan(fov*0.5)*aspect;
  vec3 d=normalize(vec3(px,py,1.0));
  float cp=cos(pitch),sp=sin(pitch),cy=cos(yaw),sy=sin(yaw);
  d=vec3(d.x, d.y*cp - d.z*sp, d.y*sp + d.z*cp);
  d=vec3(d.x*cy + d.z*sy, d.y, -d.x*sy + d.z*cy);
  float u=atan(d.x,d.z)/(2.0*3.14159265)+0.5;
  float w=acos(clamp(d.y,-1.0,1.0))/3.14159265;
  gl_FragColor=texture2D(t,vec2(u,w));
}`;

export interface LensViewer {
  setImage: (img: HTMLImageElement) => void;
  /** Fora da viewport → pula o trabalho de GL sem matar o contexto. */
  setActive: (on: boolean) => void;
  dispose: () => void;
}

export function createLensViewer(canvas: HTMLCanvasElement, yawOffset = 0): LensViewer | null {
  const gl = canvas.getContext('webgl', { antialias: true });
  if (!gl) return null;

  const sh = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return s;
  };
  const prog = gl.createProgram()!;
  gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
  gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
  gl.linkProgram(prog);
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  const U = (n: string) => gl.getUniformLocation(prog, n);
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, 1, 1, 0, gl.RGB, gl.UNSIGNED_BYTE, new Uint8Array([10, 10, 14]));

  let active = true;
  let disposed = false;

  const setImage = (img: HTMLImageElement) => {
    if (disposed) return;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  };

  const frame = (now: number) => {
    if (disposed) return;
    requestAnimationFrame(frame);
    if (!active) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth * dpr;
    const h = canvas.clientHeight * dpr;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
    }
    gl.useProgram(prog);
    gl.uniform1f(U('yaw'), 0.4 + now * 0.00006 + yawOffset);
    gl.uniform1f(U('pitch'), -0.06);
    gl.uniform1f(U('fov'), 1.25);
    gl.uniform1f(U('aspect'), canvas.clientWidth / Math.max(1, canvas.clientHeight));
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };
  requestAnimationFrame(frame);

  return {
    setImage,
    setActive: (on) => { active = on; },
    dispose: () => {
      disposed = true;
      const ext = gl.getExtension('WEBGL_lose_context');
      ext?.loseContext();
    },
  };
}
